import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonSm,
  Checkbox,
  DialogMod,
  FormSection,
  Input,
  ValidationPanel,
} from '@zawarski/eti-ui';
import { Plus, X } from 'lucide-react';
import { FieldGrid } from '@components/layout/PageScaffold';
import { phoneFormat } from '@utils/index';
import { type NotificationFormValues } from './notificationContact';
export * from './notificationContact';

interface NotificationContactFormProps {
  values: NotificationFormValues;
  onChange: (values: NotificationFormValues) => void;
  errors: Partial<Record<keyof NotificationFormValues, string>>;
  saveAttempted: boolean;
  /** Vocabulary for the triggers picker (county names for ALOC, organization names for equipment). */
  triggerOptions: string[];
  triggerLabel: { singular: string; plural: string; dialogTitle: string };
  readOnly?: boolean;
}

/**
 * Contact information / methods of contact / notification triggers - shared by
 * the ALOC (state contacts) and equipment (organization contacts) pages.
 */
const NotificationContactForm = ({
  values,
  onChange,
  errors,
  saveAttempted,
  triggerOptions,
  triggerLabel,
  readOnly,
}: NotificationContactFormProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const set = <K extends keyof NotificationFormValues>(key: K, value: NotificationFormValues[K]) =>
    onChange({ ...values, [key]: value });

  const validationGroups = useMemo(
    () => [
      {
        groupLabel: 'Contact Information',
        fields: [
          { label: 'Full name', isValid: !errors.pvFullName },
          { label: 'Phone number', isValid: !errors.pvPhoneNumber },
          { label: 'Email address', isValid: !errors.pvEmail },
          { label: 'Methods of Contact', isValid: !errors.pvMethodOfContactEmail },
          { label: triggerLabel.plural, isValid: !errors.triggers },
        ],
      },
    ],
    [errors, triggerLabel.plural],
  );

  return (
    <>
      {/* Floats in the bottom-right corner, clear of the entry page's bottom
          action bar (and its Save button), so it stays in view while the form
          scrolls. bottom-17 is the bar's 52px plus a 16px gap. */}
      {!readOnly ? (
        <div className='rep:fixed rep:right-4 rep:bottom-17 rep:z-40'>
          <ValidationPanel groups={validationGroups} hasSaveAttempted={saveAttempted} />
        </div>
      ) : null}

      <FormSection title='Contact Information'>
        <FieldGrid cols={2}>
          <Input
            label='Full name'
            placeholder='Enter full name'
            required
            readOnly={readOnly}
            value={values.pvFullName}
            onChange={(e) => set('pvFullName', e.target.value)}
            error={saveAttempted && !!errors.pvFullName}
            helperText={saveAttempted ? errors.pvFullName : undefined}
          />
          <Input
            label='Phone number'
            placeholder='Enter number'
            type='tel'
            readOnly={readOnly}
            value={values.pvPhoneNumber}
            onChange={(e) => set('pvPhoneNumber', phoneFormat(e.target.value))}
            error={saveAttempted && !!errors.pvPhoneNumber}
            helperText={saveAttempted ? errors.pvPhoneNumber : undefined}
          />
          <Input
            label='Email address'
            placeholder='Enter email address'
            type='email'
            readOnly={readOnly}
            value={values.pvEmail}
            onChange={(e) => set('pvEmail', e.target.value)}
            error={saveAttempted && !!errors.pvEmail}
            helperText={saveAttempted ? errors.pvEmail : undefined}
          />
        </FieldGrid>
      </FormSection>

      <FormSection title='Methods of Contact'>
        <div className='rep:flex rep:flex-col rep:gap-3 rep:p-4'>
          <Checkbox
            label='Text message'
            readOnly={readOnly}
            checked={values.pvMethodOfContactText}
            onCheckedChange={(checked) =>
              !readOnly && set('pvMethodOfContactText', checked === true)
            }
          />
          <Checkbox
            label='Email'
            readOnly={readOnly}
            checked={values.pvMethodOfContactEmail}
            onCheckedChange={(checked) =>
              !readOnly && set('pvMethodOfContactEmail', checked === true)
            }
          />
          {saveAttempted && errors.pvMethodOfContactEmail ? (
            <p className='rep:text-xs rep:text-destructive'>{errors.pvMethodOfContactEmail}</p>
          ) : null}
        </div>
      </FormSection>

      <FormSection
        title='Notification Triggers'
        suffixButtons={
          readOnly
            ? undefined
            : [
                <ButtonSm
                  key='add'
                  onClick={() => setPickerOpen(true)}
                  endIcon={<Plus size={16} />}>
                  Add Triggers
                </ButtonSm>,
              ]
        }>
        <div className='rep:flex rep:flex-col rep:gap-3 rep:p-4'>
          {values.triggers.length ? (
            <div className='rep:flex rep:flex-wrap rep:gap-2'>
              {values.triggers.map((trigger) => (
                <span
                  key={trigger}
                  className='rep:inline-flex rep:items-center rep:gap-1 rep:rounded-md rep:border rep:border-border rep:bg-card rep:py-1.5 rep:pr-1.5 rep:pl-3 rep:text-sm rep:text-card-foreground'>
                  {trigger}
                  {!readOnly ? (
                    <button
                      type='button'
                      aria-label={`Remove ${trigger}`}
                      onClick={() =>
                        set(
                          'triggers',
                          values.triggers.filter((t) => t !== trigger),
                        )
                      }
                      className='rep:cursor-pointer rep:rounded-full rep:p-0.5 rep:text-muted-foreground rep:hover:bg-muted rep:hover:text-foreground'>
                      <X className='rep:size-3.5' />
                    </button>
                  ) : null}
                </span>
              ))}
            </div>
          ) : (
            <p className='rep:text-sm rep:text-muted-foreground'>
              No {triggerLabel.plural.toLowerCase()} selected.
            </p>
          )}
          {saveAttempted && errors.triggers ? (
            <p className='rep:text-xs rep:text-destructive'>{errors.triggers}</p>
          ) : null}
        </div>
      </FormSection>

      <TriggerPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={triggerLabel.dialogTitle}
        options={triggerOptions}
        selected={values.triggers}
        onConfirm={(next) => {
          set('triggers', next);
          setPickerOpen(false);
        }}
      />
    </>
  );
};

const TriggerPickerDialog = ({
  open,
  onOpenChange,
  title,
  options,
  selected,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  options: string[];
  selected: string[];
  onConfirm: (next: string[]) => void;
}) => {
  const [draft, setDraft] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (open) {
      setDraft(selected);
      setSearch('');
    }
  }, [open, selected]);
  const visible = options.filter((o) => o.toLowerCase().includes(search.trim().toLowerCase()));
  const toggle = (name: string) =>
    setDraft((d) => (d.includes(name) ? d.filter((x) => x !== name) : [...d, name]));
  return (
    <DialogMod
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      subtitle={`${draft.length} selected`}
      size='sm'
      leftBtnTitle='Cancel'
      rightBtnTitle='Apply'
      onClick={() => onConfirm(draft)}>
      <div className='rep:flex rep:flex-col rep:gap-3 rep:px-4 rep:py-4'>
        <div className='rep:flex rep:items-center rep:gap-2'>
          <Input placeholder='Search' value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant='outline' size='sm' onClick={() => setDraft(visible)}>
            All
          </Button>
          <Button variant='outline' size='sm' onClick={() => setDraft([])}>
            None
          </Button>
        </div>
        <div className='rep:grid rep:max-h-80 rep:grid-cols-1 rep:gap-1 rep:overflow-y-auto rep:sm:grid-cols-2'>
          {visible.map((name) => (
            <Checkbox
              key={name}
              label={name}
              checked={draft.includes(name)}
              onCheckedChange={() => toggle(name)}
            />
          ))}
          {!visible.length ? (
            <p className='rep:text-sm rep:text-muted-foreground'>No matches.</p>
          ) : null}
        </div>
      </div>
    </DialogMod>
  );
};

export default NotificationContactForm;
